import {ComboboxProvider, PopoverDisclosure, PopoverProvider, usePopoverStore} from '@ariakit/react'
import {ReactElement} from 'react'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {useTranslation} from '@/translations/i18n'
import {SelectVirtualList} from '@/components/ui/select-atoms/SelectAtoms'
import {Checkbox} from '@/components/commons/checkbox/ChekBox'
import {UserSmall} from '@/features/users/types'
import {
    StyledUsersMultiSelectComboboxList,
    StyledUsersMultiSelectPopover,
    StyledUsersMultiSelectPopoverInput,
    StyledUsersMultiSelectPopoverOption
} from './style'
import {useInfiniteUsersSearchableList} from '@/features/users/hooks/useInfiniteUsersSearchableList'

interface UsersMultiSelectPopoverProps {
    trigger: ReactElement
    toggleClick: (value: UserSmall) => void
    selectedUserIds: UserSmall['id'][]
    onClose?: () => void
    modal?: boolean
}

export const UsersMultiSelectPopover = ({
    trigger,
    toggleClick,
    selectedUserIds,
    onClose,
    modal = false
}: UsersMultiSelectPopoverProps) => {
    const {t} = useTranslation()
    const popoverStore = usePopoverStore({
        placement: 'bottom-start'
    })
    const usersQuery = useInfiniteUsersSearchableList({
        limit: 10
    })

    const handlePopoverOpenChange = (open: boolean) => {
        if (!open && onClose) {
            onClose()
        }
    }

    const handleToggleClick = (item: UserSmall, event: React.MouseEvent) => {
        event.stopPropagation() // Prevent event bubbling that might close popover
        toggleClick(item)
    }

    return (
        <PopoverProvider store={popoverStore} setOpen={handlePopoverOpenChange}>
            <PopoverDisclosure render={trigger} />
            <StyledUsersMultiSelectPopover modal={modal} gutter={8}>
                <ComboboxProvider open setValue={usersQuery.onSearch} resetValueOnHide>
                    <StyledUsersMultiSelectPopoverInput
                        hasCloseCombobox
                        placeholder={t('commons:search_placeholder')}
                    />
                    <StyledUsersMultiSelectComboboxList>
                        {usersQuery?.isLoading ? (
                            <>
                                {Array.from({length: 6}, (_, index) => (
                                    <Skeleton key={index} height={32} />
                                ))}
                            </>
                        ) : usersQuery.isError ? (
                            <InputHelpText error={t('errors:default')} />
                        ) : (
                            <SelectVirtualList
                                data={usersQuery.remappedData}
                                totalCount={usersQuery.remappedData.length}
                                overscan={100}
                                style={{height: 200}}
                                endReached={() => usersQuery.hasNextPage && usersQuery.fetchNextPage()}
                                itemContent={(itemIndex, item) => {
                                    const isChecked = selectedUserIds.includes(item.id)

                                    return (
                                        <StyledUsersMultiSelectPopoverOption
                                            key={itemIndex}
                                            variant="ghost"
                                            shape="text"
                                            onClick={e => handleToggleClick(item, e)}
                                        >
                                            <Checkbox id={`user-${item.id}`} checked={isChecked} />
                                            {item.fullName}
                                        </StyledUsersMultiSelectPopoverOption>
                                    )
                                }}
                            />
                        )}
                    </StyledUsersMultiSelectComboboxList>
                </ComboboxProvider>
            </StyledUsersMultiSelectPopover>
        </PopoverProvider>
    )
}
